/**
 * One customer's situation, as the desk sees it.
 *
 * The screen an ADV opens when a customer telephones about an order that has
 * been held: the credit line, what is overdue, and the documents behind the
 * figure. It answers "why is this company blocked" in one look, and it offers
 * the two things that unblock them — record the payment that has arrived, and
 * recompute the figure if it looks wrong.
 *
 * **The recompute button is not a refresh.** The credit figure is derived from
 * the documents, and this writes it back to the customer's record. It exists
 * because the column is read by the orders module's credit check: if a
 * settlement is ever written outside billing, the column drifts and a
 * customer is refused for money they do not owe. An ADV needs a way to prove
 * that is or is not the cause, and it needs the settlement permission,
 * because it changes what a customer may order.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { download, useAction, useResource } from "@hardauto/core-data";
import { day } from "@hardauto/core-i18n";
import { Button, ErrorState, LoadingState, Page, useToast } from "@hardauto/core-ui";

import { BillingApi } from "../api";
import { SettleDialog } from "../SettleDialog";
import { StatementPanels } from "../StatementPanels";
import { NAMESPACE } from "../i18n";
import { BILLING_PATHS } from "../paths";
import "./billing.css";

export function StatementScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new BillingApi(client), [client]);
  const action = useAction();
  const toast = useToast();
  const maySettle = useCan("billing.settlement.write");

  const [settling, setSettling] = useState(false);

  const statementFetcher = useCallback(() => api.statement(id), [api, id]);
  const statement = useResource(statementFetcher);

  // The schedule is the open documents, not a page of everything: the point
  // of this screen is what is still owed.
  const openFetcher = useCallback(
    () => api.list({ client_id: id, unpaid_only: true, limit: 200 }),
    [api, id],
  );
  const open = useResource(openFetcher);
  const documents = useMemo(() => (open.data?.items ?? []).map((row) => row.document), [open.data]);

  if (statement.loading) return <LoadingState label={t("statement.loading")} />;
  if (!statement.data) {
    return (
      <ErrorState
        title={t("error.title")}
        description={t("error.body")}
        retryLabel={t("error.retry")}
        onRetry={statement.reload}
      />
    );
  }

  const facts = statement.data;

  return (
    <Page
      title={facts.company_name}
      description={t("statement.description", { code: facts.client_code })}
      eyebrow={t("statement.asOf", { date: day(facts.as_of, locale) })}
      back={<Link to={BILLING_PATHS.ledger}>{t("document.back")}</Link>}
      actions={
        <>
          <Button
            busy={action.isBusy("statement-pdf")}
            busyLabel={t("document.pdf.downloading")}
            onClick={() =>
              void action.run("statement-pdf", async () => {
                try {
                  download(
                    await api.statementPdf(id),
                    `${t("export.statementFilename")}-${facts.client_code}.pdf`,
                  );
                } catch {
                  toast.show(t("statement.schedule.downloadFailed"), { tone: "danger" });
                }
              })
            }
          >
            {t("statement.schedule.download")}
          </Button>
          {maySettle ? (
            <Button variant="primary" onClick={() => setSettling(true)}>
              {t("settle.open")}
            </Button>
          ) : null}
        </>
      }
    >
      <StatementPanels
        statement={facts}
        documents={documents}
        documentsLoading={open.loading}
        documentsFailed={open.failed}
        onRetryDocuments={open.reload}
        creditAction={
          maySettle ? (
            <Button
              size="sm"
              busy={action.isBusy("refresh")}
              busyLabel={t("statement.refresh")}
              onClick={() =>
                void action.run("refresh", async () => {
                  try {
                    await api.refreshCredit(id);
                    toast.show(t("statement.refreshed"), { tone: "success" });
                    statement.reload();
                  } catch {
                    toast.show(t("statement.refreshFailed"), { tone: "danger" });
                  }
                })
              }
            >
              {t("statement.refresh")}
            </Button>
          ) : undefined
        }
      />

      {maySettle ? (
        <SettleDialog
          api={api}
          open={settling}
          onClose={() => setSettling(false)}
          clientId={id}
          clientLabel={`${facts.company_name} (${facts.client_code})`}
          documents={documents}
          onRecorded={() => {
            statement.reload();
            open.reload();
          }}
        />
      ) : null}
    </Page>
  );
}
